/**
 * Shown when a signed-in account holds no application role.
 *
 * Authentication succeeded and authorization did not, which is an easy thing
 * to mistake for a broken app. The page names every role so the user can ask
 * for the right one, and offers sign-out because a token minted before the
 * role was assigned will not carry it — refreshing the page is not enough.
 */
import { useMsal } from "@azure/msal-react";

import { ROLE_ADMIN, ROLE_PBI_MANAGER, ROLE_PBI_MON, useRoles } from "../auth/access";

export default function NoAccessPage() {
  const { instance } = useMsal();
  const { signedInAs } = useRoles();

  return (
    <div className="max-w-2xl mx-auto mt-12">
      <div className="bg-surface-raised border border-edge rounded-lg p-8">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-50 border border-amber-300 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-amber-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
              />
            </svg>
          </div>

          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-content">No access assigned</h1>
            <p className="mt-2 text-sm text-content-muted">
              You are signed in{signedInAs ? ` as ${signedInAs}` : ""}, but your account has not been
              granted a role in Power BI Monitor. Nothing is available until an administrator
              assigns one.
            </p>

            <dl className="mt-6 space-y-4 text-sm">
              <div>
                <dt className="font-medium text-content">{ROLE_ADMIN}</dt>
                <dd className="text-content-muted">
                  Full access, including the SOX control pages, the raw audit log, e-discovery and
                  system administration.
                </dd>
              </div>
              <div>
                <dt className="font-medium text-content">{ROLE_PBI_MANAGER}</dt>
                <dd className="text-content-muted">
                  Read-only access to workspaces, reports, gateways and dataflows, across the
                  whole tenant.
                </dd>
              </div>
              <div>
                <dt className="font-medium text-content">{ROLE_PBI_MON}</dt>
                <dd className="text-content-muted">
                  Read-only access to the monitored workspaces and the reports inside them.
                  Unmonitored workspaces are not listed and not readable.
                </dd>
              </div>
            </dl>

            <p className="mt-6 text-sm text-content-muted">
              Ask an administrator to assign a role in the Entra portal, under Enterprise
              applications → Torrens PowerBI Monitor → Users and groups. Once it is assigned, sign
              out and back in — an existing session keeps the token it was issued, and that token
              does not carry the new role.
            </p>

            <button
              type="button"
              onClick={() => instance.logoutRedirect()}
              className="mt-6 px-4 py-2 rounded-lg bg-accent text-accent-fg text-sm font-medium hover:bg-accent-hover transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
